import { useRef, useState, type FormEvent } from "react";
import { useAuth } from "../app/AuthProvider";
import { useTheme } from "../app/ThemeProvider";
import {
  useMe,
  useUpdateLanguagePreference,
  useUpdatePushPreference,
  useUpdateThemePreference,
  useUpdateOwnProfile,
  useChangeOwnPassword,
  useUploadAvatar,
  useDeleteAvatar,
  useNotifications,
  useMarkNotificationRead,
  useDeleteAllNotifications,
  useEmailStatus,
  useTrialNoticePreview,
  useSendTrialNoticeSample,
  useSendTrialSummarySample,
  useSendTestEmail,
  useTenantSmtp,
  useSaveTenantSmtp,
  useDeleteTenantSmtp,
  useTestTenantSmtp,
  useSetTenantSmtpEnabled,
  useTenantBillingEmail,
  useSaveTenantBillingEmail,
  type AppNotification,
} from "../api/hooks";
import { Button, Card, Chip, ErrorNotice, Field, Input, Notice, PageHeader, Select, Spinner } from "../components/ui";
import { SecurityPanel } from "../components/SecurityPanel";
import { THEME_LABELS } from "../lib/theme";
import { tenantRoleLabel } from "../lib/format";
import type { ThemePreference } from "@deca/shared-types";

/**
 * Ficha del usuario — Zona B.
 *
 * Pestañas que concentran toda la gestión de la cuenta propia:
 *   • Perfil      — nombre, email, avatar (inicial)
 *   • Seguridad   — cambio de contraseña, MFA, rol
 *   • Notificaciones — historial in-app + toggles de avisos
 *   • Correo      — solo super-admin: estado del SMTP y envío de prueba
 *   • Correo de empresa — ADMIN del tenant: su propio SMTP (con respaldo en el de la plataforma)
 *   • Facturación — ADMIN del tenant: correo de facturación (el que ve Stripe)
 */

// ── Helpers ──────────────────────────────────────────────────────────────────

const LANGUAGES = [
  { value: "es", label: "Español" },
  { value: "en", label: "English" },
];

const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  DECA_CREATED: "Documento creado",
  DECA_VERSION_UPDATED: "Nueva versión",
  DECA_REPLACED: "Documento sustituido",
  DECA_DOWNLOAD_DISABLED: "Descarga desactivada",
  DECA_SIGNED: "Documento firmado",
  SUBSCRIPTION_RENEWAL_REMINDER: "Recordatorio de renovación",
  SUBSCRIPTION_BLOCKED: "Suscripción bloqueada",
  TRIAL_ENDING: "Fin de prueba próximo",
  TRIAL_EXPIRED: "Prueba terminada",
  INVITATION_SENT: "Invitación enviada",
  GENERIC: "Aviso",
};

function getInitials(name?: string): string {
  if (!name) return "?";
  return name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0] ?? "")
    .join("")
    .toUpperCase();
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("es-ES", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ── Pestaña: Perfil ───────────────────────────────────────────────────────────

function AvatarUploader({ avatarUrl, fullName }: { avatarUrl: string | null | undefined; fullName: string | null | undefined }) {
  const uploadAvatar = useUploadAvatar();
  const deleteAvatar = useDeleteAvatar();
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError(null);

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setAvatarError("Solo se aceptan imágenes JPEG, PNG o WebP.");
      return;
    }
    if (file.size > 512 * 1024) {
      setAvatarError("La imagen no puede superar los 512 KB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target?.result as string;
      const base64 = dataUrl.split(",")[1] ?? "";
      setPreview(dataUrl);
      await uploadAvatar.mutateAsync({ contentType: file.type, base64 });
    };
    reader.readAsDataURL(file);
  }

  const src = preview ?? avatarUrl ?? null;

  return (
    <div className="flex items-center gap-4">
      {/* Avatar */}
      <div className="relative">
        {src ? (
          <img
            src={src}
            alt="Avatar"
            className="h-16 w-16 rounded-full object-cover shadow"
          />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-500 text-2xl font-bold text-white shadow">
            {getInitials(fullName ?? undefined)}
          </div>
        )}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-ink-700 text-white hover:bg-ink-900 dark:border-ink-900"
          title="Cambiar avatar"
        >
          <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={handleFileChange} />
      </div>

      <div className="space-y-1">
        {avatarError && <p className="text-xs text-signal-danger">{avatarError}</p>}
        {uploadAvatar.isError && <ErrorNotice error={uploadAvatar.error} />}
        {src && (
          <button
            type="button"
            onClick={() => { setPreview(null); void deleteAvatar.mutate(); }}
            className="text-xs text-ink-400 underline hover:text-signal-danger"
            disabled={deleteAvatar.isPending}
          >
            Eliminar avatar
          </button>
        )}
        <p className="text-xs text-ink-400">JPEG, PNG o WebP · máx 512 KB</p>
      </div>
    </div>
  );
}

function TabPerfil() {
  const me = useMe();
  const updateProfile = useUpdateOwnProfile();
  const { resolved, setPreference } = useTheme();
  const updateTheme = useUpdateThemePreference();
  const updateLanguage = useUpdateLanguagePreference();

  const [fullName, setFullName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (me.isLoading) return <Spinner label="Cargando perfil…" />;
  if (me.isError) return <ErrorNotice error={me.error} onRetry={() => void me.refetch()} />;

  const user = me.data!;
  const currentName = fullName ?? user.fullName ?? "";
  const currentEmail = email ?? user.email ?? "";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    const patch: { fullName?: string; email?: string } = {};
    if (currentName !== user.fullName) patch.fullName = currentName;
    if (currentEmail !== user.email) patch.email = currentEmail;
    if (Object.keys(patch).length === 0) return;
    await updateProfile.mutateAsync(patch);
    setSaved(true);
    setFullName(null);
    setEmail(null);
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Avatar + datos personales */}
      <Card className="lg:col-span-2">
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <AvatarUploader avatarUrl={(user as { avatarUrl?: string | null }).avatarUrl} fullName={user.fullName} />
          <div>
            <p className="text-lg font-semibold text-ink-900 dark:text-ink-100">{user.fullName}</p>
            <p className="text-sm text-ink-500 dark:text-ink-400">{user.email}</p>
            <p className="mt-1 text-xs text-ink-400">
              {user.isSuperAdmin ? "Super-administrador" : tenantRoleLabel(user.role)}
            </p>
          </div>
        </div>
      </Card>

      {/* Formulario datos */}
      <Card>
        <h2 className="micro-label mb-3">Datos personales</h2>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
          <Field label="Nombre completo" htmlFor="profile-name">
            <Input
              id="profile-name"
              value={currentName}
              onChange={(e) => {
                setFullName(e.target.value);
                setSaved(false);
              }}
              placeholder="Tu nombre"
            />
          </Field>
          <Field label="Correo electrónico" htmlFor="profile-email">
            <Input
              id="profile-email"
              type="email"
              value={currentEmail}
              onChange={(e) => {
                setEmail(e.target.value);
                setSaved(false);
              }}
              placeholder="tu@correo.com"
            />
          </Field>
          {updateProfile.isError && <ErrorNotice error={updateProfile.error} />}
          {saved && (
            <p className="text-xs text-signal-ok dark:text-teal-300">Perfil guardado correctamente.</p>
          )}
          <Button type="submit" variant="primary" disabled={updateProfile.isPending}>
            {updateProfile.isPending ? "Guardando…" : "Guardar cambios"}
          </Button>
        </form>
      </Card>

      {/* Apariencia + Idioma */}
      <div className="space-y-4">
        <Card>
          <h2 className="micro-label mb-3">Apariencia</h2>
          <Field
            label="Tema"
            htmlFor="set-theme"
            hint={`Ahora en modo ${resolved === "dark" ? "oscuro" : "claro"}.`}
          >
            <Select
              id="set-theme"
              value={user.themePreference ?? "SYSTEM"}
              onChange={(e) => setPreference(e.target.value as ThemePreference, { persist: false })}
            >
              {(["LIGHT", "DARK", "SYSTEM"] as ThemePreference[]).map((opt) => (
                <option key={opt} value={opt}>
                  {THEME_LABELS[opt]}
                </option>
              ))}
            </Select>
          </Field>
          <p className="mt-2 text-xs text-ink-500 dark:text-ink-400">
            «Sistema» sigue el modo del dispositivo.
          </p>
          {updateTheme.isError && <ErrorNotice error={updateTheme.error} />}
          {updateTheme.isSuccess && (
            <p className="mt-1 text-xs text-signal-ok dark:text-teal-300">Tema guardado.</p>
          )}
        </Card>

        <Card>
          <h2 className="micro-label mb-3">Idioma de la interfaz</h2>
          <Field label="Idioma" htmlFor="set-language" hint="No afecta al documento legal.">
            <Select
              id="set-language"
              value={user.preferredLanguage ?? "es"}
              onChange={(e) => updateLanguage.mutate(e.target.value)}
            >
              {LANGUAGES.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </Select>
          </Field>
          {updateLanguage.isError && <ErrorNotice error={updateLanguage.error} />}
        </Card>
      </div>
    </div>
  );
}

// ── Pestaña: Seguridad ────────────────────────────────────────────────────────

function TabSeguridad() {
  const me = useMe();
  const changePassword = useChangeOwnPassword();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (me.isLoading) return <Spinner label="Cargando…" />;
  if (me.isError) return <ErrorNotice error={me.error} onRetry={() => void me.refetch()} />;

  const user = me.data!;

  async function handlePassword(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    setDone(false);
    if (next !== confirm) {
      setLocalError("Las contraseñas nuevas no coinciden.");
      return;
    }
    if (next.length < 8) {
      setLocalError("La contraseña nueva debe tener al menos 8 caracteres.");
      return;
    }
    await changePassword.mutateAsync({ currentPassword: current, newPassword: next });
    setCurrent("");
    setNext("");
    setConfirm("");
    setDone(true);
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Cambio de contraseña */}
      <Card>
        <h2 className="micro-label mb-3">Cambiar contraseña</h2>
        <form onSubmit={(e) => void handlePassword(e)} className="space-y-3">
          <Field label="Contraseña actual" htmlFor="pw-current">
            <Input
              id="pw-current"
              type="password"
              value={current}
              onChange={(e) => {
                setCurrent(e.target.value);
                setDone(false);
              }}
              autoComplete="current-password"
              placeholder="Tu contraseña actual"
            />
          </Field>
          <Field label="Nueva contraseña" htmlFor="pw-new">
            <Input
              id="pw-new"
              type="password"
              value={next}
              onChange={(e) => {
                setNext(e.target.value);
                setDone(false);
              }}
              autoComplete="new-password"
              placeholder="Mínimo 8 caracteres"
            />
          </Field>
          <Field label="Confirmar nueva contraseña" htmlFor="pw-confirm">
            <Input
              id="pw-confirm"
              type="password"
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
                setDone(false);
              }}
              autoComplete="new-password"
              placeholder="Repite la nueva contraseña"
            />
          </Field>
          {localError && (
            <p className="text-xs text-signal-danger dark:text-red-400">{localError}</p>
          )}
          {changePassword.isError && <ErrorNotice error={changePassword.error} />}
          {done && (
            <p className="text-xs text-signal-ok dark:text-teal-300">
              Contraseña cambiada correctamente.
            </p>
          )}
          <Button type="submit" variant="primary" disabled={changePassword.isPending}>
            {changePassword.isPending ? "Cambiando…" : "Cambiar contraseña"}
          </Button>
        </form>
      </Card>

      {/* Verificación en dos pasos (opcional, personal) */}
      <SecurityPanel user={user} />

      {/* Información de seguridad */}
      <Card>
        <h2 className="micro-label mb-3">Información de la cuenta</h2>
        <dl className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <dt className="text-ink-600 dark:text-ink-300">Segundo factor</dt>
            <dd>
              {user.mfaEnabled ? (
                <Chip tone="ok">App de autenticación</Chip>
              ) : user.pinEnabled ? (
                <Chip tone="ok">PIN de acceso</Chip>
              ) : (
                <Chip tone="neutral">Sin activar</Chip>
              )}
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-ink-600 dark:text-ink-300">Correo electrónico</dt>
            <dd className="font-mono text-xs">{user.email}</dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-ink-600 dark:text-ink-300">Rol en esta empresa</dt>
            <dd>{tenantRoleLabel(user.role)}</dd>
          </div>
          {user.isSuperAdmin && (
            <div className="flex items-center justify-between">
              <dt className="text-ink-600 dark:text-ink-300">Super-admin</dt>
              <dd>
                <Chip tone="ok">Sí</Chip>
              </dd>
            </div>
          )}
        </dl>
      </Card>
    </div>
  );
}

// ── Pestaña: Notificaciones ───────────────────────────────────────────────────

function NotificationRow({ n, onRead }: { n: AppNotification; onRead: (id: string) => void }) {
  const unread = !n.readAt;
  return (
    <li
      className={`flex items-start gap-3 rounded-lg p-3 transition-colors ${
        unread
          ? "bg-brand-50 dark:bg-brand-900/30"
          : "bg-transparent opacity-70"
      }`}
    >
      <span
        className={`mt-1 h-2 w-2 shrink-0 rounded-full ${unread ? "bg-brand-500" : "bg-ink-300 dark:bg-ink-600"}`}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink-900 dark:text-ink-100">{n.title}</p>
        <p className="mt-0.5 text-xs text-ink-500 dark:text-ink-400">{n.body}</p>
        <p className="mt-1 text-xs text-ink-400 dark:text-ink-500">
          <span className="mr-2 rounded bg-ink-100 px-1 py-0.5 text-[10px] font-medium dark:bg-ink-800">
            {NOTIFICATION_TYPE_LABELS[n.type] ?? n.type}
          </span>
          {formatDate(n.createdAt)}
        </p>
      </div>
      {unread && (
        <button
          type="button"
          onClick={() => onRead(n.id)}
          className="shrink-0 text-xs text-brand-600 underline hover:text-brand-700 dark:text-brand-400"
        >
          Marcar leída
        </button>
      )}
    </li>
  );
}

function TabNotificaciones() {
  const notifications = useNotifications();
  const markRead = useMarkNotificationRead();
  const deleteAll = useDeleteAllNotifications();
  const updatePush = useUpdatePushPreference();
  const me = useMe();

  if (notifications.isLoading || me.isLoading) return <Spinner label="Cargando notificaciones…" />;
  if (notifications.isError) return <ErrorNotice error={notifications.error} onRetry={() => void notifications.refetch()} />;

  const items = notifications.data ?? [];
  const unreadCount = items.filter((n) => !n.readAt).length;
  const user = me.data;

  async function handleMarkAll() {
    const unread = items.filter((n) => !n.readAt);
    await Promise.all(unread.map((n) => markRead.mutateAsync(n.id)));
  }

  function handleDeleteAll() {
    if (!window.confirm("¿Borrar todas tus notificaciones? Esta acción no se puede deshacer.")) return;
    deleteAll.mutate();
  }

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      {/* Historial */}
      <div className="lg:col-span-2">
        <Card>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="micro-label">
              Historial
              {unreadCount > 0 && (
                <span className="ml-2 rounded-full bg-brand-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                  {unreadCount}
                </span>
              )}
            </h2>
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => void handleMarkAll()}
                  className="text-xs text-brand-600 underline hover:text-brand-700 dark:text-brand-400"
                >
                  Marcar todas como leídas
                </button>
              )}
              {items.length > 0 && (
                <button
                  type="button"
                  onClick={handleDeleteAll}
                  disabled={deleteAll.isPending}
                  className="text-xs text-red-600 underline hover:text-red-700 disabled:opacity-50 dark:text-red-400"
                >
                  {deleteAll.isPending ? "Borrando…" : "Borrar todas"}
                </button>
              )}
            </div>
          </div>

          {items.length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-400">No tienes notificaciones todavía.</p>
          ) : (
            <ul className="space-y-1 divide-y divide-ink-100 dark:divide-ink-800">
              {items.map((n) => (
                <NotificationRow key={n.id} n={n} onRead={(id) => void markRead.mutate(id)} />
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Configuración de avisos */}
      <div className="space-y-4">
        <Card>
          <h2 className="micro-label mb-3">Avisos push</h2>
          <label className="flex cursor-pointer items-center gap-3 text-sm text-ink-700 dark:text-ink-200">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500 dark:border-ink-600"
              checked={user?.pushNotificationsEnabled ?? true}
              onChange={(e) => updatePush.mutate(e.target.checked)}
            />
            <span>Recibir avisos en segundo plano</span>
          </label>
          <p className="mt-2 text-xs text-ink-500 dark:text-ink-400">
            Cambios de versión, desactivación de descargas, renovaciones próximas y suscripción
            bloqueada.
          </p>
          {updatePush.isError && <ErrorNotice error={updatePush.error} />}
        </Card>

        <Card>
          <h2 className="micro-label mb-2">Qué recibirás</h2>
          <ul className="space-y-1 text-xs text-ink-500 dark:text-ink-400">
            {Object.entries(NOTIFICATION_TYPE_LABELS).map(([key, label]) => (
              <li key={key} className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-400" />
                {label}
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

// ── Pestaña: Correo (solo super-admin) ────────────────────────────────────────

const TRIAL_KIND_LABELS: Record<string, string> = {
  DAYS_7: "Quedan 7 días o menos",
  DAYS_1: "Queda 1 día o menos",
  EXPIRED: "Prueba terminada",
};

/** Simulación (solo lectura): qué avisos de fin de trial saldrían ahora y a quién. */
function TrialSampleForm({ defaultTo, smtpConfigured }: { defaultTo: string; smtpConfigured: boolean }) {
  const sample = useSendTrialNoticeSample();
  const [to, setTo] = useState<string | null>(null);
  const [kind, setKind] = useState<"DAYS_7" | "DAYS_1" | "EXPIRED">("DAYS_7");
  const target = to ?? defaultTo;
  const result = sample.data;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!target.trim()) return;
    sample.mutate({ to: target.trim(), kind });
  }

  return (
    <form onSubmit={handleSubmit} className="mb-4 rounded-lg border border-ink-100 p-3 dark:border-ink-700">
      <h3 className="mb-2 text-sm font-medium">Enviarme una muestra</h3>
      <p className="mb-3 text-xs text-ink-500 dark:text-ink-400">
        Envía un aviso de ejemplo (asunto con [MUESTRA]) a la dirección que elijas. No toca ningún cliente ni queda
        registrado como aviso enviado.
      </p>
      <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <Field label="Enviar a" htmlFor="trial-sample-to">
          <Input
            id="trial-sample-to"
            type="email"
            value={target}
            onChange={(e) => {
              setTo(e.target.value);
              sample.reset();
            }}
            placeholder="tu@correo.com"
            disabled={!smtpConfigured}
          />
        </Field>
        <Field label="Tipo de aviso" htmlFor="trial-sample-kind">
          <select
            id="trial-sample-kind"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as typeof kind);
              sample.reset();
            }}
            disabled={!smtpConfigured}
            className="h-10 w-full rounded-md border border-ink-200 bg-white px-2 text-sm dark:border-ink-600 dark:bg-ink-800"
          >
            <option value="DAYS_7">Quedan 7 días</option>
            <option value="DAYS_1">Queda 1 día</option>
            <option value="EXPIRED">Prueba terminada</option>
          </select>
        </Field>
        <Button type="submit" variant="primary" disabled={!smtpConfigured || !target.trim() || sample.isPending}>
          {sample.isPending ? "Enviando…" : "Enviar muestra"}
        </Button>
      </div>
      {sample.isError ? (
        <div className="mt-3">
          <ErrorNotice error={sample.error} />
        </div>
      ) : null}
      {result ? (
        <div className="mt-3">
          <Notice tone={result.ok ? "ok" : "danger"}>
            <p className="font-medium">{result.ok ? "Enviada" : "No se pudo enviar"}</p>
            <p className="mt-1 break-words">{result.message}</p>
            {result.code ? <p className="mt-1 font-mono text-xs">Código: {result.code}</p> : null}
          </Notice>
        </div>
      ) : null}
    </form>
  );
}

/** Muestra del resumen diario que recibe el super-admin (datos ficticios, no escribe nada). */
function TrialSummarySampleForm({ defaultTo, smtpConfigured }: { defaultTo: string; smtpConfigured: boolean }) {
  const sample = useSendTrialSummarySample();
  const [to, setTo] = useState<string | null>(null);
  const target = to ?? defaultTo;
  const result = sample.data;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!target.trim()) return;
    sample.mutate({ to: target.trim() });
  }

  return (
    <form onSubmit={handleSubmit} className="mb-4 rounded-lg border border-ink-100 p-3 dark:border-ink-700">
      <h3 className="mb-2 text-sm font-medium">Enviarme una muestra del resumen</h3>
      <p className="mb-3 text-xs text-ink-500 dark:text-ink-400">
        Es el correo que recibes cada día cuando se avisa a algún cliente. La muestra usa clientes e incidencias
        ficticios (asunto con [MUESTRA]); no toca ningún cliente ni crea notificaciones.
      </p>
      <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <Field label="Enviar a" htmlFor="trial-summary-sample-to">
          <Input
            id="trial-summary-sample-to"
            type="email"
            value={target}
            onChange={(e) => {
              setTo(e.target.value);
              sample.reset();
            }}
            placeholder="tu@correo.com"
            disabled={!smtpConfigured}
          />
        </Field>
        <Button type="submit" variant="primary" disabled={!smtpConfigured || !target.trim() || sample.isPending}>
          {sample.isPending ? "Enviando…" : "Enviar muestra del resumen"}
        </Button>
      </div>
      {sample.isError ? (
        <div className="mt-3">
          <ErrorNotice error={sample.error} />
        </div>
      ) : null}
      {result ? (
        <div className="mt-3">
          <Notice tone={result.ok ? "ok" : "danger"}>
            <p className="font-medium">{result.ok ? "Enviada" : "No se pudo enviar"}</p>
            <p className="mt-1 break-words">{result.message}</p>
            {result.code ? <p className="mt-1 font-mono text-xs">Código: {result.code}</p> : null}
          </Notice>
        </div>
      ) : null}
    </form>
  );
}

function TrialNoticesCard() {
  const me = useMe();
  const preview = useTrialNoticePreview();
  const data = preview.data;
  return (
    <Card className="lg:col-span-2">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="micro-label">Avisos de fin de prueba (simulación)</h2>
        <Button type="button" variant="quiet" onClick={() => void preview.refetch()} disabled={preview.isFetching}>
          {preview.isFetching ? "Calculando…" : "Actualizar"}
        </Button>
      </div>
      <p className="mb-3 text-xs text-ink-500 dark:text-ink-400">
        Cada día a las 04:10 el sistema avisa a los administradores de cada tenant a 7 días, a 1 día y al terminar la
        prueba (solo avisa: no bloquea). Aquí ves qué saldría ahora mismo, sin enviar nada.
      </p>
      <TrialSampleForm defaultTo={me.data?.email ?? ""} smtpConfigured={data?.emailConfigured ?? false} />
      <TrialSummarySampleForm defaultTo={me.data?.email ?? ""} smtpConfigured={data?.emailConfigured ?? false} />
      {preview.isLoading ? <Spinner label="Calculando…" /> : null}
      {preview.isError ? <ErrorNotice error={preview.error} onRetry={() => void preview.refetch()} /> : null}
      {data ? (
        <>
          {!data.emailConfigured ? (
            <Notice tone="warn">Sin SMTP configurado: los avisos solo llegarán dentro de la aplicación.</Notice>
          ) : null}
          {data.items.length === 0 ? (
            <p className="mt-2 text-sm text-ink-600 dark:text-ink-300">Ahora mismo no hay ningún aviso pendiente.</p>
          ) : (
            <ul className="mt-2 divide-y divide-ink-100 text-sm dark:divide-ink-700">
              {data.items.map((item) => (
                <li key={`${item.tenantId}-${item.kind}`} className="py-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium">{item.tenantName}</span>
                    <Chip tone={item.alreadySent ? "neutral" : item.kind === "EXPIRED" ? "danger" : "warn"}>
                      {item.alreadySent ? "Ya enviado" : (TRIAL_KIND_LABELS[item.kind] ?? item.kind)}
                    </Chip>
                  </div>
                  <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
                    Fin de prueba: {new Date(item.trialEndsAt).toLocaleDateString("es-ES")} ·{" "}
                    {item.recipients.length > 0
                      ? `Destinatarios: ${item.recipients.join(", ")}`
                      : "Sin administradores activos: no hay a quién avisar"}
                  </p>
                </li>
              ))}
            </ul>
          )}
          {data.skippedExpiredLongAgo > 0 ? (
            <p className="mt-3 text-xs text-ink-500 dark:text-ink-400">
              {data.skippedExpiredLongAgo} tenant(s) con la prueba vencida hace más de 2 días no recibirán aviso (para no
              enviar correos antiguos).
            </p>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}

function TabCorreo() {
  const me = useMe();
  const status = useEmailStatus();
  const sendTest = useSendTestEmail();
  const [to, setTo] = useState<string | null>(null);

  if (me.isLoading || status.isLoading) return <Spinner label="Cargando…" />;
  if (status.isError) return <ErrorNotice error={status.error} onRetry={() => void status.refetch()} />;

  const info = status.data!;
  const target = to ?? me.data?.email ?? "";

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!target.trim()) return;
    sendTest.mutate({ to: target.trim() });
  }

  const result = sendTest.data;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <h2 className="micro-label mb-3">Correo de la plataforma</h2>
        {info.configured ? (
          <dl className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-ink-600 dark:text-ink-300">Estado</dt>
              <dd>
                <Chip tone="ok">Configurado</Chip>
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-ink-600 dark:text-ink-300">Servidor</dt>
              <dd className="font-mono text-xs">
                {info.host}:{info.port}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-ink-600 dark:text-ink-300">Cifrado</dt>
              <dd>{info.secure ? "TLS directo" : "STARTTLS"}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-ink-600 dark:text-ink-300">Remitente</dt>
              <dd className="font-mono text-xs">
                {info.fromName} &lt;{info.fromAddress}&gt;
              </dd>
            </div>
          </dl>
        ) : (
          <Notice tone="warn">
            El servidor no tiene SMTP configurado: no se envía ningún correo (ni invitaciones ni bienvenidas). Faltan
            variables <code>SMTP_*</code> o <code>EMAIL_FROM_ADDRESS</code> en el entorno de producción.
          </Notice>
        )}
        <p className="mt-3 text-xs text-ink-500 dark:text-ink-400">
          Por seguridad, el usuario y la contraseña del SMTP nunca se muestran aquí.
        </p>
      </Card>

      <Card>
        <h2 className="micro-label mb-3">Enviar correo de prueba</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <Field
            label="Destinatario"
            htmlFor="test-email-to"
            hint="Se envía con el SMTP de la plataforma. Usa un correo tuyo."
          >
            <Input
              id="test-email-to"
              type="email"
              value={target}
              onChange={(e) => {
                setTo(e.target.value);
                sendTest.reset();
              }}
              placeholder="tu@correo.com"
              disabled={!info.configured}
            />
          </Field>
          <Button type="submit" variant="primary" disabled={!info.configured || !target.trim() || sendTest.isPending}>
            {sendTest.isPending ? "Enviando…" : "Enviar correo de prueba"}
          </Button>
        </form>

        {sendTest.isError && (
          <div className="mt-3">
            <ErrorNotice error={sendTest.error} />
          </div>
        )}
        {result && (
          <div className="mt-3">
            <Notice tone={result.ok ? "ok" : "danger"}>
              <p className="font-medium">{result.ok ? "Enviado" : "No se pudo enviar"}</p>
              <p className="mt-1 break-words">{result.message}</p>
              {result.code ? <p className="mt-1 font-mono text-xs">Código: {result.code}</p> : null}
              {result.ok ? (
                <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
                  Revisa también la carpeta de spam. Si no llega, mira el registro de envíos de tu proveedor de correo.
                </p>
              ) : null}
            </Notice>
          </div>
        )}
      </Card>

      <TrialNoticesCard />
    </div>
  );
}

function TabCorreoEmpresa() {
  const me = useMe();
  const smtp = useTenantSmtp();
  const save = useSaveTenantSmtp();
  const remove = useDeleteTenantSmtp();
  const test = useTestTenantSmtp();
  const setEnabled = useSetTenantSmtpEnabled();

  const [form, setForm] = useState<{
    host: string;
    port: string;
    secure: boolean;
    username: string;
    password: string;
    fromAddress: string;
    fromName: string;
  } | null>(null);
  const [testTo, setTestTo] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (smtp.isLoading) return <Spinner label="Cargando…" />;
  if (smtp.isError) return <ErrorNotice error={smtp.error} onRetry={() => void smtp.refetch()} />;

  const info = smtp.data!;
  const values = form ?? {
    host: info.host ?? "",
    port: String(info.port ?? 587),
    secure: info.secure ?? false,
    username: info.username ?? "",
    password: "",
    fromAddress: info.fromAddress ?? "",
    fromName: info.fromName ?? "",
  };
  const patch = (changes: Partial<typeof values>) => {
    setForm({ ...values, ...changes });
    save.reset();
    test.reset();
  };
  const target = testTo ?? me.data?.email ?? "";
  const dirty = form !== null;

  function handleSave(e: FormEvent) {
    e.preventDefault();
    const port = Number(values.port);
    save.mutate(
      {
        host: values.host.trim(),
        port,
        secure: values.secure,
        username: values.username.trim(),
        ...(values.password ? { password: values.password } : {}),
        fromAddress: values.fromAddress.trim(),
        ...(values.fromName.trim() ? { fromName: values.fromName.trim() } : {}),
      },
      { onSuccess: () => setForm(null) },
    );
  }

  const testResult = test.data;
  const canTest = info.configured && !dirty;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <h2 className="micro-label mb-3">Servidor de correo de tu empresa</h2>
        <p className="mb-3 text-xs text-ink-500 dark:text-ink-400">
          Si lo configuras, las invitaciones a tu equipo saldrán desde tu propio correo. Si falla o no lo activas, se
          envían igualmente con el correo de la plataforma.
        </p>
        {!info.encryptionKeyConfigured ? (
          <div className="mb-3">
            <Notice tone="warn">
              El servidor aún no está preparado para guardar contraseñas de correo de forma segura. Pide al
              administrador de la plataforma que lo active.
            </Notice>
          </div>
        ) : null}
        <form onSubmit={handleSave} className="space-y-3">
          <Field label="Servidor SMTP" htmlFor="smtp-host" hint="Por ejemplo smtp.tuempresa.es">
            <Input id="smtp-host" value={values.host} onChange={(e) => patch({ host: e.target.value })} required />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Puerto" htmlFor="smtp-port" hint="25, 465, 587 o 2525">
              <Select id="smtp-port" value={values.port} onChange={(e) => patch({ port: e.target.value })}>
                {["587", "465", "2525", "25"].map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Cifrado" htmlFor="smtp-secure">
              <Select
                id="smtp-secure"
                value={values.secure ? "tls" : "starttls"}
                onChange={(e) => patch({ secure: e.target.value === "tls" })}
              >
                <option value="starttls">STARTTLS (587)</option>
                <option value="tls">TLS directo (465)</option>
              </Select>
            </Field>
          </div>
          <Field label="Usuario" htmlFor="smtp-user">
            <Input
              id="smtp-user"
              value={values.username}
              onChange={(e) => patch({ username: e.target.value })}
              autoComplete="off"
              required
            />
          </Field>
          <Field
            label="Contraseña"
            htmlFor="smtp-pass"
            hint={info.hasPassword ? "Déjala vacía para conservar la guardada." : "Se guarda cifrada y no se vuelve a mostrar."}
          >
            <Input
              id="smtp-pass"
              type="password"
              value={values.password}
              onChange={(e) => patch({ password: e.target.value })}
              autoComplete="new-password"
              required={!info.hasPassword}
            />
          </Field>
          <Field label="Dirección del remitente" htmlFor="smtp-from" hint="Debe pertenecer a tu servidor de correo.">
            <Input
              id="smtp-from"
              type="email"
              value={values.fromAddress}
              onChange={(e) => patch({ fromAddress: e.target.value })}
              required
            />
          </Field>
          <Field label="Nombre del remitente (opcional)" htmlFor="smtp-from-name">
            <Input id="smtp-from-name" value={values.fromName} onChange={(e) => patch({ fromName: e.target.value })} />
          </Field>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="primary" disabled={save.isPending || !info.encryptionKeyConfigured}>
              {save.isPending ? "Guardando…" : "Guardar"}
            </Button>
            {info.configured && !confirmDelete ? (
              <Button type="button" variant="danger" onClick={() => setConfirmDelete(true)}>
                Eliminar
              </Button>
            ) : null}
            {confirmDelete ? (
              <>
                <Button
                  type="button"
                  variant="danger"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(undefined, { onSettled: () => setConfirmDelete(false) })}
                >
                  Confirmar eliminación
                </Button>
                <Button type="button" onClick={() => setConfirmDelete(false)}>
                  Cancelar
                </Button>
              </>
            ) : null}
          </div>
        </form>
        {save.isError ? (
          <div className="mt-3">
            <ErrorNotice error={save.error} />
          </div>
        ) : null}
        {save.isSuccess && !dirty ? (
          <div className="mt-3">
            <Notice tone="ok">Guardado. Ahora envía una prueba para poder activarlo.</Notice>
          </div>
        ) : null}
        {remove.isError ? (
          <div className="mt-3">
            <ErrorNotice error={remove.error} />
          </div>
        ) : null}
      </Card>

      <div className="space-y-4">
        <Card>
          <h2 className="micro-label mb-3">Estado</h2>
          {!info.configured ? (
            <p className="text-sm text-ink-600 dark:text-ink-300">
              Sin configurar: tus correos salen con el correo de la plataforma.
            </p>
          ) : (
            <dl className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-ink-600 dark:text-ink-300">Uso</dt>
                <dd>{info.enabled ? <Chip tone="ok">Activado</Chip> : <Chip tone="neutral">Desactivado</Chip>}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-ink-600 dark:text-ink-300">Última prueba</dt>
                <dd>
                  {info.lastTestOk === true ? (
                    <Chip tone="ok">Correcta</Chip>
                  ) : info.lastTestOk === false ? (
                    <Chip tone="danger">Fallida</Chip>
                  ) : (
                    <Chip tone="warn">Pendiente</Chip>
                  )}
                </dd>
              </div>
              {info.lastTestOk === false && info.lastTestError ? (
                <p className="break-words text-xs text-ink-500 dark:text-ink-400">{info.lastTestError}</p>
              ) : null}
              {info.lastFailureAt ? (
                <div>
                  <p className="text-xs text-ink-600 dark:text-ink-300">
                    Último fallo al enviar: {new Date(info.lastFailureAt).toLocaleString("es-ES")}. Ese correo se envió
                    con el correo de la plataforma.
                  </p>
                  {info.lastFailureError ? (
                    <p className="mt-1 break-words text-xs text-ink-500 dark:text-ink-400">{info.lastFailureError}</p>
                  ) : null}
                </div>
              ) : null}
              <div className="pt-1">
                {info.enabled ? (
                  <Button
                    type="button"
                    disabled={setEnabled.isPending}
                    onClick={() => setEnabled.mutate({ enabled: false })}
                  >
                    Desactivar
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="primary"
                    disabled={setEnabled.isPending || info.lastTestOk !== true || dirty}
                    onClick={() => setEnabled.mutate({ enabled: true })}
                  >
                    Activar
                  </Button>
                )}
                {!info.enabled && info.lastTestOk !== true ? (
                  <p className="mt-2 text-xs text-ink-500 dark:text-ink-400">
                    Para activarlo hace falta una prueba correcta.
                  </p>
                ) : null}
              </div>
            </dl>
          )}
          {setEnabled.isError ? (
            <div className="mt-3">
              <ErrorNotice error={setEnabled.error} />
            </div>
          ) : null}
        </Card>

        <Card>
          <h2 className="micro-label mb-3">Enviar correo de prueba</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (target.trim()) test.mutate({ to: target.trim() });
            }}
            className="space-y-3"
          >
            <Field
              label="Destinatario"
              htmlFor="smtp-test-to"
              hint="Se envía con el servidor de tu empresa. Usa un correo tuyo."
            >
              <Input
                id="smtp-test-to"
                type="email"
                value={target}
                onChange={(e) => {
                  setTestTo(e.target.value);
                  test.reset();
                }}
                disabled={!canTest}
              />
            </Field>
            <Button type="submit" variant="primary" disabled={!canTest || !target.trim() || test.isPending}>
              {test.isPending ? "Probando…" : "Probar conexión y enviar"}
            </Button>
            {dirty ? (
              <p className="text-xs text-ink-500 dark:text-ink-400">Guarda los cambios antes de probar.</p>
            ) : null}
          </form>
          {test.isError ? (
            <div className="mt-3">
              <ErrorNotice error={test.error} />
            </div>
          ) : null}
          {testResult ? (
            <div className="mt-3">
              <Notice tone={testResult.ok ? "ok" : "danger"}>
                <p className="font-medium">{testResult.ok ? "Enviado" : "No se pudo enviar"}</p>
                <p className="mt-1 break-words">{testResult.message}</p>
                {testResult.ok ? (
                  <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
                    Revisa también la carpeta de spam. Si lo recibes, ya puedes activarlo.
                  </p>
                ) : null}
              </Notice>
            </div>
          ) : null}
        </Card>
      </div>
    </div>
  );
}

function TabFacturacion() {
  const view = useTenantBillingEmail();
  const save = useSaveTenantBillingEmail();
  const [draft, setDraft] = useState<string | null>(null);

  if (view.isLoading) return <Spinner label="Cargando…" />;
  if (view.isError) return <ErrorNotice error={view.error} onRetry={() => void view.refetch()} />;

  const info = view.data!;
  const value = draft ?? info.billingEmail ?? "";
  const dirty = draft !== null && draft.trim() !== (info.billingEmail ?? "");

  function handleSave(e: FormEvent) {
    e.preventDefault();
    save.mutate({ billingEmail: value.trim() === "" ? null : value.trim() }, { onSuccess: () => setDraft(null) });
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <h2 className="micro-label mb-3">Correo de facturación</h2>
        <p className="mb-3 text-xs text-ink-500 dark:text-ink-400">
          Es el correo al que se envían las facturas y los recibos de tu suscripción. Puede ser distinto del de tu
          acceso, por ejemplo el de tu administración o tu contable. Si lo dejas vacío se usa el del administrador.
        </p>
        <form onSubmit={handleSave} className="space-y-3">
          <Field
            label="Correo de facturación"
            htmlFor="billing-email"
            hint={info.adminEmail ? `Si está vacío se usará ${info.adminEmail}.` : undefined}
          >
            <Input
              id="billing-email"
              type="email"
              value={value}
              onChange={(e) => {
                setDraft(e.target.value);
                save.reset();
              }}
              placeholder="facturacion@tuempresa.es"
              autoComplete="off"
            />
          </Field>
          {save.isError ? <ErrorNotice error={save.error} /> : null}
          {save.isSuccess && !dirty ? <Notice tone="ok">Correo de facturación guardado.</Notice> : null}
          <Button type="submit" variant="primary" disabled={!dirty || save.isPending}>
            {save.isPending ? "Guardando…" : "Guardar"}
          </Button>
        </form>
      </Card>
      <Card>
        <h2 className="micro-label mb-3">Correo que se usa ahora</h2>
        <p className="ident text-sm">{info.effectiveEmail ?? "—"}</p>
        <p className="mt-2 text-xs text-ink-500 dark:text-ink-400">
          {info.billingEmail ? "Es el correo de facturación configurado." : "Es el del administrador (no hay correo de facturación propio)."}
        </p>
      </Card>
    </div>
  );
}

// ── Raíz ─────────────────────────────────────────────────────────────────────

type Tab = "perfil" | "seguridad" | "notificaciones" | "correo" | "correo-empresa" | "facturacion";

const TABS: { id: Tab; label: string }[] = [
  { id: "perfil", label: "Perfil" },
  { id: "seguridad", label: "Seguridad" },
  { id: "notificaciones", label: "Notificaciones" },
  { id: "correo", label: "Correo" },
  { id: "correo-empresa", label: "Correo de empresa" },
  { id: "facturacion", label: "Facturación" },
];

export function SettingsPage() {
  const auth = useAuth();
  const me = useMe();
  const [activeTab, setActiveTab] = useState<Tab>("perfil");
  // La pestaña "Correo" (estado del SMTP y prueba de envío) es solo de super-admin.
  // "Correo de empresa" (SMTP propio) es del ADMIN de un tenant activo (o super-admin dentro de uno).
  const canManageTenantSmtp =
    !!me.data?.tenantId && (me.data.role === "ADMIN" || me.data.isSuperAdmin === true);
  const visibleTabs = TABS.filter((tab) => {
    if (tab.id === "correo") return me.data?.isSuperAdmin === true;
    if (tab.id === "correo-empresa" || tab.id === "facturacion") return canManageTenantSmtp;
    return true;
  });
  const notifications = useNotifications();
  const unreadCount = (notifications.data ?? []).filter((n) => !n.readAt).length;

  return (
    <>
      <PageHeader
        eyebrow="Cuenta"
        title="Mi ficha"
        description="Tu perfil, seguridad y notificaciones. Viajan contigo entre el panel y la app del conductor."
      />

      {/* Pestañas */}
      <nav className="mb-4 flex gap-1 border-b border-ink-200 dark:border-ink-700" aria-label="Pestañas de ajustes">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`relative px-4 py-2 text-sm font-medium transition-colors focus:outline-none ${
              activeTab === tab.id
                ? "border-b-2 border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-300"
                : "text-ink-500 hover:text-ink-800 dark:text-ink-400 dark:hover:text-ink-100"
            }`}
            aria-selected={activeTab === tab.id}
            role="tab"
          >
            {tab.label}
            {tab.id === "notificaciones" && unreadCount > 0 && (
              <span className="ml-1.5 rounded-full bg-brand-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* Contenido */}
      {activeTab === "perfil" && <TabPerfil />}
      {activeTab === "seguridad" && <TabSeguridad />}
      {activeTab === "notificaciones" && <TabNotificaciones />}
      {activeTab === "correo" && me.data?.isSuperAdmin === true && <TabCorreo />}
      {activeTab === "correo-empresa" && canManageTenantSmtp && <TabCorreoEmpresa />}
      {activeTab === "facturacion" && canManageTenantSmtp && <TabFacturacion />}

      {/* Cerrar sesión siempre visible */}
      <div className="mt-6 border-t border-ink-200 pt-4 dark:border-ink-700">
        <Button variant="danger" onClick={auth.logout}>
          Cerrar sesión
        </Button>
      </div>
    </>
  );
}
